'use client';

import {
  AttributeSide,
  AttributeSideDynamic,
} from '@eliadmin/core/src/components/categories/attributes/attribute';
import { AttributeCategory } from '@eliadmin/core/src/components/categories/attributes/attribute-category';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { Card } from '@eliadmin/core/src/components/ui/card';
import { Separator } from '@eliadmin/core/src/components/ui/separator';
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetOverlay,
  SheetPortal,
  SheetTrigger,
  SheetTitle,
} from '@eliadmin/core/src/components/ui/sheet';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useCategoryStore } from '@eliadmin/core/src/stores/category-store';
import { useGlobalStore } from '@eliadmin/core/src/stores/global-store';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { HiOutlineAdjustmentsHorizontal } from 'react-icons/hi2';
import { LuX } from 'react-icons/lu';

const FilterSeparator = () => <Separator className="w-full last-of-type:hidden" />;

const closeLabels: Record<string, string> = {
  sk: 'Zavrieť filter',
  cs: 'Zavřít filtr',
  cz: 'Zavřít filtr',
  de: 'Filter schließen',
  hu: 'Szűrő bezárása',
  pl: 'Zamknij filtr',
  en: 'Close filter',
};

export function CategoryFilterSheet() {
  const templateSettings = useGlobalStore(state => state.templateSettings);
  const categoryTree = useCategoryStore(state => state.categoryTree);
  const category = useCategoryStore(state => state.category);
  const { t } = useTranslations();
  const locale = useLocaleStore(state => state.currentLocale);
  const clearFilter = useCategoryStore(s => s.clearFilters);

  return (
    <Sheet>
      <SheetTrigger asChild>
        <Button variant="outline" className="w-full whitespace-nowrap md:w-fit">
          <HiOutlineAdjustmentsHorizontal className="h-5 w-5 lg:h-6 lg:w-6" /> {t('text174')}
        </Button>
      </SheetTrigger>
      <SheetPortal>
        <SheetOverlay className="bg-black/30 backdrop-blur-none" />
        <SheetContent
          aria-describedby={undefined}
          side="right"
          className={cn(
            'flex w-full flex-col items-start justify-start gap-6 p-6 pb-0 sm:max-w-[392px] sm:rounded-l-[24px]',
            'ea-category-filter-sheet__content',
          )}
          showCloseIcon={false}
        >
          <Card
            className={cn(
              'relative flex h-screen w-full flex-col items-start justify-start gap-6 p-0',
              'ea-category-filter-sheet',
            )}
          >
            <div
              className={cn(
                'flex w-full items-center justify-between',
                'ea-category-filter-sheet__expand-button',
              )}
            >
              <SheetTitle asChild>
                <h4 className="text-2xl font-medium tracking-tight">{t('text174')}</h4>
              </SheetTitle>
              <SheetClose
                aria-label={
                  closeLabels[(locale ?? 'en').replaceAll('_', '-').split('-')[0].toLowerCase()] ??
                  closeLabels.en
                }
              >
                <LuX className="text-[#636363]" size={24} />
              </SheetClose>
            </div>

            <div
              className={cn(
                'flex w-full flex-col gap-3 overflow-y-auto pb-32',
                'ea-category-filter-sheet__content',
              )}
            >
              {!!templateSettings.category?.price_in_filter?.enabled && (
                <>
                  <FilterSeparator />
                  <AttributeSide
                    name={t('text340')}
                    type="price"
                    attribute={{ type: 'status', type2: 'statuses' }}
                    order={templateSettings.category.price_in_filter.order}
                    className={{ wrapper: cn('', 'ea-category-filter-sheet__price') }}
                  />
                  <FilterSeparator />
                </>
              )}
              {/* manufacturers */}
              {!!templateSettings.category?.manufacturers_in_filter?.enabled &&
                !!category.manufacturers?.length && (
                  <>
                    <AttributeSide
                      name={t('text195')}
                      type="generic"
                      attribute={{ type: 'manufacturer', type2: 'manufacturers' }}
                      order={templateSettings.category.manufacturers_in_filter.order}
                      className={{ wrapper: cn('', 'ea-category-filter-sheet__manufacturers') }}
                    />
                    <FilterSeparator />
                  </>
                )}

              {/* statuses */}
              {!!templateSettings.category?.statuses_in_filter?.enabled &&
                !!category.statuses?.length && (
                  <>
                    <AttributeSide
                      name={t('text518')}
                      type="generic"
                      attribute={{ type: 'status', type2: 'statuses' }}
                      order={templateSettings.category?.categories_in_filter?.order}
                      className={{ wrapper: cn('', 'ea-category-filter-sheet__statuses') }}
                    />
                    <FilterSeparator />
                  </>
                )}

              {/* availabilities */}
              {!!templateSettings.category?.availability_in_filter?.enabled &&
                !!category.availabilities?.length && (
                  <>
                    <AttributeSide
                      name={t('text339')}
                      type="generic"
                      attribute={{ type: 'availability', type2: 'availabilities' }}
                      order={templateSettings.category.availability_in_filter.order}
                      className={{ wrapper: cn('', 'ea-category-filter-sheet__availabilities') }}
                    />
                    <FilterSeparator />
                  </>
                )}

              {/* attributes */}
              {!!templateSettings.category?.attributes_in_filter?.enabled && (
                // <div className={`order-${templateSettings.category.attributes_in_filter.order}`}>
                <>
                  {category.attributes?.map((attribute, i) => (
                    <>
                      <AttributeSideDynamic
                        key={`attribute-${attribute.id ?? attribute.name}-${i}`}
                        name={attribute.name}
                        attribute={attribute as any}
                        className={{ wrapper: cn('', 'ea-category-filter-sheet__attributes') }}
                      />
                      <FilterSeparator />
                    </>
                  ))}
                </>
              )}

              {!!templateSettings.category?.suppliers_in_filter?.enabled && (
                <>
                  <AttributeSide
                    name={t('text718')}
                    type="generic"
                    attribute={{ type: 'supplier', type2: 'suppliers' }}
                    className={{ wrapper: cn('', 'ea-category-filter-sheet__suppliers') }}
                    order={templateSettings.category.suppliers_in_filter.order}
                  />
                  <FilterSeparator />
                </>
              )}
              {!!templateSettings.category?.suppliers_location_in_filter?.enabled && (
                <>
                  <AttributeSide
                    name={t('text728')}
                    type="supplierLocation"
                    attribute={{ type: 'supplier', type2: 'suppliers' }}
                    className={{ wrapper: cn('', 'ea-category-filter-sheet__suppliers_location') }}
                    order={templateSettings.category?.suppliers_in_filter?.order}
                  />
                  <FilterSeparator />
                </>
              )}
            </div>

            <div
              className={cn(
                'fixed bottom-0 right-0 flex h-fit w-full flex-col justify-end gap-2 bg-background px-6 py-4 sm:max-w-[392px] sm:rounded-bl-[24px]',
                'ea-category-filter-sheet__footer-container',
              )}
            >
              <SheetClose asChild>
                <Button className="w-full">{t('text634')}</Button>
              </SheetClose>
            </div>
          </Card>
        </SheetContent>
      </SheetPortal>
    </Sheet>
  );
}
